import { useEffect, useState } from 'react'
import { useUserStore } from 'src/stores/user-store'
import { paths } from 'src/routes/paths'
import {
  STORAGE_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_TYPE_KEY,
  AUTH_TYPE_MSAL,
} from 'src/constants'
import { useRouter } from 'src/hooks'
import { Spin } from 'antd'
import { isValidToken } from '../context/utils'
import { refreshToken } from 'src/services'
import { encrypt } from 'src/utils/storage'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import toast from 'react-hot-toast'
import { t } from 'i18next'
import { useMsalTokenRefresh } from '../hooks/use-msal-token-refresh'

type AuthGuardProps = {
  children: React.ReactNode
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter()
  const user = useUserStore()
  const { instance, accounts, inProgress } = useMsal()

  const { refreshToken: refreshMsalToken, checkAndRefreshIfNeeded } =
    useMsalTokenRefresh()

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      const authType = localStorage.getItem(AUTH_TYPE_KEY)
      const session = localStorage.getItem(STORAGE_KEY)
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY)

      const isOnLoginPage = window.location.pathname === paths.auth.login
      const searchParams = new URLSearchParams(window.location.search)
      const alreadyRedirecting = searchParams.has('returnTo')

      if (authType === AUTH_TYPE_MSAL) {
        if (inProgress !== 'none') {
          return
        }

        if (!instance.getActiveAccount() && accounts.length === 0) {
          return
        }

        if (accounts.length > 0) {
          try {
            if (session && isValidToken(session)) {
              await checkAndRefreshIfNeeded()
              setIsAuthenticated(true)
              setIsCheckingAuth(false)
              return
            }

            const newToken = await refreshMsalToken(true)

            if (newToken) {
              setIsAuthenticated(true)
              setIsCheckingAuth(false)
              return
            } else {
              throw new Error('Failed to acquire MSAL token')
            }
          } catch (error) {
            console.error('MSAL authentication error:', error)

            if (error instanceof InteractionRequiredAuthError) {
              try {
                const popupResponse = await instance.acquireTokenPopup({
                  scopes: ['user.read'],
                })
                if (popupResponse.idToken) {
                  localStorage.setItem(
                    STORAGE_KEY,
                    encrypt(popupResponse.idToken)
                  )
                  setIsAuthenticated(true)
                  setIsCheckingAuth(false)
                  return
                }
              } catch (popupError) {
                console.error('MSAL popup error:', popupError)
                toast.error(t('error.unspecific'))
              }
            }

            if (!isOnLoginPage && !alreadyRedirecting) {
              user.clearUser()
              localStorage.removeItem(AUTH_TYPE_KEY)
              localStorage.removeItem(STORAGE_KEY)

              const params = new URLSearchParams({
                returnTo: window.location.pathname,
              }).toString()

              const href = `${paths.auth.login}?${params}`
              router.replace(href)
            }
          }
        } else {
          if (inProgress === 'none' && !isOnLoginPage && !alreadyRedirecting) {
            user.clearUser()
            localStorage.removeItem(AUTH_TYPE_KEY)
            localStorage.removeItem(STORAGE_KEY)

            const params = new URLSearchParams({
              returnTo: window.location.pathname,
            }).toString()

            const href = `${paths.auth.login}?${params}`
            router.replace(href)
          }
        }
      } else {
        const hasValidAccessToken = session && isValidToken(session)

        if (!hasValidAccessToken && !refreshTokenValue) {
          if (!isOnLoginPage && !alreadyRedirecting) {
            user.clearUser()

            const params = new URLSearchParams({
              returnTo: window.location.pathname,
            }).toString()

            const href = `${paths.auth.login}?${params}`
            router.replace(href)
          }
        } else if (!hasValidAccessToken && refreshTokenValue) {
          try {
            const newToken = await refreshToken()

            if (newToken) {
              localStorage.setItem(STORAGE_KEY, encrypt(newToken))
              setIsAuthenticated(true)
            } else {
              throw new Error('No token received from refresh')
            }
          } catch (error) {
            if (!isOnLoginPage && !alreadyRedirecting) {
              user.clearUser()
              localStorage.clear()

              const params = new URLSearchParams({
                returnTo: window.location.pathname,
              }).toString()

              const href = `${paths.auth.login}?${params}`
              router.replace(href)
            }
          }
        } else {
          setIsAuthenticated(true)
        }
      }

      setIsCheckingAuth(false)
    }

    checkAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance, user, router, inProgress, refreshMsalToken])

  if (inProgress !== 'none' || isCheckingAuth || isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin />
      </div>
    )
  }

  return isAuthenticated ? children : null
}
